import React from "react";
import { RouteComponentProps } from "react-router";

import CloseIcon from "@material-ui/icons/Close";
import ReplayIcon from "@material-ui/icons/Replay";
import TimerIcon from "@material-ui/icons/Timer";

import { PresenterToolBar } from "./PresenterToolBar";
import { PageNotFound } from "../errors/pageNotFound";
import { ServerFailed } from "../errors/serverFailed";

import { BuilderAssetService } from "../../services/BuilderAssetService";
import { ServerResponse } from "../../services/server/OldWebClient";
import { PresenterAppBar } from "./PresenterAppBar";

import PresenterPreferences, { TimeMode, ToolbarLocation, toToolbarLocation } from "../../entities/PresenterPreferences";
import { PresenterPreferencesService } from "../../services/PresenterPreferencesService";
import { ApplicationUser } from "../../entities/ApplicationUser";
import AppConfig from "../../entities/AppConfig";
import { TelemetryService } from "../../services/TelemetryService";
import { useAppConfig } from "../../utilities/useAppConfig";
import { useUser } from "../../utilities/useUser";
import { useAuthenticatedUser } from "../../utilities/useAuthenticatedUser";
import { BuilderAsset } from "../../entities/BuilderAsset";
import { useOfflineAssets } from "../offline/useOfflineAssets";
import { useLocalStorage } from "../../utilities/useLocalStorage";
import { useOfflineStatus } from "../../utilities/useOfflineStatus";
import { EditorHelper } from "../builder/editor/EditorHelper";
import { ServerError, ValidationError, SuccessResult } from "../../services/server/ServerResponses";
import { makeStyles, LinearProgress, Drawer, List, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Divider, TextField, Button, Grid, Paper, CircularProgress, Typography, Snackbar, IconButton } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
	container: {
		background: "#efefef",
		lineHeight: "1.75em",
	},
	progressBar: {
		position: "fixed",
		top: 0,
		left: 0,
		width: "100%",
		height: 7,
	},
	appBar: {
		color: "#6b6b6b",
		background: "#FFFFFF",
		boxShadow: "unset",
		paddingTop: "4px",
	},
	sermonContent: {
		[theme.breakpoints.up("sm")]: {
			margin: theme.spacing(4),
			marginBottom: theme.spacing(12),
			padding: theme.spacing(4),
		},
		[theme.breakpoints.down("xs")]: {
			paddingLeft: theme.spacing(2),
			paddingRight: theme.spacing(2),
			marginBottom: 65,
			marginTop: 10,
			width: "100%",
			overflowWrap: "break-word",
		},
	},
	grow: {
		flex: 1,
	},
	settingsListing: {
		width: 250,
	},
	nested: {
		paddingLeft: theme.spacing(4),
	},
	formControl: {
		margin: theme.spacing(3),
	},
	group: {
		margin: `${theme.spacing()}px 0`,
	},
	noMargin: {
		margin: 0,
	},
	textField: {
		marginLeft: theme.spacing(0),
		marginRight: theme.spacing(0),
	},
	resetButton: {
		margin: "15px auto",
	},
	fontSizingWrapper: {
		display: "flex",
		padding: "7px 15px",
	},
	fontPrecentage: {
		padding: "5px 10px",
		fontWeight: "bolder",
	},
	buttonIcon: {
		padding: 5,
	},
	title: {
		whiteSpace: "nowrap",
		overflow: "hidden",
		width: "100%",
		textOverflow: "ellipsis",
	},
	timerIcon: {
		color: "#FFF",
		fontSize: 25,
		paddingTop: 3,
	},
	timerIconWrapper: {
		position: "fixed",
		right: 0,
		top: "10%",
		padding: 5,
		backgroundColor: "#666",
		borderTopLeftRadius: 3,
		borderBottomLeftRadius: 3,
		cursor: "pointer",
		transition: "all 0.3s ease 0s",
		opacity: 1,
		"&:hover": {
			opacity: 0.7,
			transition: "all 0.3s ease 0s",
		},
	},
	"@global": {
		blockquote: {
			overflow: "hidden",
			paddingRight: "1.5em",
			paddingLeft: "1.5em",
			marginLeft: 0,
			fontStyle: "italic",
			borderLeft: "5px solid #ccc",
		},
		h1: {
			lineHeight: "1.4em",
		},
		h2: {
			lineHeight: "1.4em",
		},
	},
}));

interface Params {
	id: string;
}

interface Props extends RouteComponentProps<Params> {
	onSignOut: () => void;
	onSignIn: (user: ApplicationUser) => void;
	user: ApplicationUser;
	config: AppConfig;
	getBuilderAsset: () => Promise<SuccessResult<BuilderAsset> | ServerError>;
	getPresenterPreferences: () => Promise<SuccessResult<PresenterPreferences> | ServerError>;
	savePresenterPreferences: (
		prefs: PresenterPreferences
	) => Promise<ServerError | SuccessResult<PresenterPreferences> | ValidationError>;
}

type PropsWithClasses = Props & { classes: ReturnType<typeof useStyles> };

type State = {
	loading: boolean;
	pageNotFound: boolean;
	serverFailed: boolean;
	snackbarMessage: string;
	snackbarOpen: boolean;
	scrollProgress: number;
	title: string;
	body: string;
	isFullScreen: boolean;
	settingsOpen: boolean;
	settingsBarPlacementListOpen: boolean;
	countdownTime: number;
	fontSize: number;
	timeMode: TimeMode;
	timerEnabled: boolean;
	toolbarLocation: ToolbarLocation;
	reset: number;
	anchorElFont?: HTMLElement;
};

class HomeComponent extends React.Component<PropsWithClasses, State> {
	constructor(props:PropsWithClasses) {
		super(props);
		this.state = {
			loading: true,
			serverFailed: false,
			pageNotFound: false,
			snackbarMessage: "",
			snackbarOpen: false,
			scrollProgress: 0,
			title: "",
			body: "",
			isFullScreen: false,
			settingsOpen: false,
			settingsBarPlacementListOpen: false,
			countdownTime: 30,
			fontSize: 20,
			timeMode: TimeMode.CountDown,
			timerEnabled: true,
			toolbarLocation: ToolbarLocation.Bottom,
			reset: 0,
		};
	}

	async componentDidMount() {
		document.title = "Sermon Maker - Presenter";

		const builderAssetResponse = await this.props.getBuilderAsset();
		if (ServerResponse.isError(builderAssetResponse)) {
			if (builderAssetResponse.statusCode === 404) {
				this.setState({ pageNotFound: true, loading: false });
			}
			if (builderAssetResponse.statusCode === 500) {
				this.setState({ serverFailed: true, loading: false });
			}
			return;
		}

		const builderAsset = builderAssetResponse.data;
		document.title = `${builderAsset.name}`;
		this.setState({ body: builderAsset.text, title: `${builderAsset.name}`, loading: false });

		let presenterPreferencesResponse = await this.props.getPresenterPreferences();
		if (ServerResponse.isError(presenterPreferencesResponse)) {
			this.setState({
				snackbarOpen: true,
				snackbarMessage: "Loading your preferences failed. Please try again.",
			});
			return;
		}

		const preferences = presenterPreferencesResponse.data;
		this.setState({
			countdownTime: preferences.countdownTime ? preferences.countdownTime : this.state.countdownTime,
			fontSize: preferences.fontSize ? preferences.fontSize : this.state.fontSize,
			timeMode: preferences.timeMode ? preferences.timeMode : this.state.timeMode,
			timerEnabled: preferences.timerEnabled ? preferences.timerEnabled : this.state.timerEnabled,
			toolbarLocation: preferences.toolbarLocation ? preferences.toolbarLocation : this.state.toolbarLocation,
		});

		window.onscroll = this.updateScrollProgress;
		document.body.onscroll = this.updateScrollProgress;

		let now = new Date();
		TelemetryService.updateContactField("sc_cloud_presenter", now.toString());
	}

	componentDidUpdate() {
		var contentContainer = document.getElementById("editor-content-container");
		if (contentContainer?.firstChild !== null) {
			EditorHelper.getEditorSlides();
		}
	}

	htmlStringToElement(html: string) {
		var template = document.createElement("template");
		template.innerHTML = html;
		return template.content.childNodes;
	}

	setAnchorElFont = (anchorElFont: HTMLElement | undefined) => {
		this.setState({
			anchorElFont,
		});
	};

	updateScrollProgress = () => {
		const element = document.querySelector("#content");
		if (element && document.documentElement && document.body) {
			const innerHeight = window.innerHeight;
			const rect = element.getBoundingClientRect();
			const rectHtmlHeight = rect.height;
			const scrollRange = Math.ceil(rectHtmlHeight - innerHeight);
			const scrollY = document.documentElement.scrollTop || document.body.scrollTop;
			const scrollProgress = Math.floor((scrollY / scrollRange) * 100);
			this.setState({ scrollProgress });
		}
	};

	toggleDrawer = async (open: boolean) => {
		this.setState({ settingsOpen: open });
		if (open) {
			return;
		} else {
			const response = await this.props.savePresenterPreferences({
				countdownTime: this.state.countdownTime,
				fontSize: this.state.fontSize,
				timeMode: this.state.timeMode,
				timerEnabled: this.state.timerEnabled,
				toolbarLocation: this.state.toolbarLocation,
			});
			if (ServerResponse.isError(response)) {
				this.setState({
					snackbarOpen: true,
					snackbarMessage: "Saving your preferences failed. Please try again.",
				});
				return;
			}
		}
	};

	toggleBarLocationOptions = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		e.stopPropagation();

		this.setState({
			settingsBarPlacementListOpen: !this.state.settingsBarPlacementListOpen,
		});
	};

	handleBarLocationChange = async (e: any) => {
		e.preventDefault();
		e.stopPropagation();
		let value = e.target.value as string;

		this.setState({
			toolbarLocation: toToolbarLocation(value),
		});
	};

	onTimeModeChange = async (e: React.ChangeEvent<{}>, value: string) => {
		let timeMode = value as TimeMode;

		let countdownTime = 0;
		if (timeMode === TimeMode.CountDown) {
			countdownTime = 30;
		}
		this.setState({ timeMode: timeMode, countdownTime: countdownTime });

		const response = this.props.savePresenterPreferences({
			countdownTime: this.state.countdownTime,
			fontSize: this.state.fontSize,
			timeMode: timeMode,
			timerEnabled: this.state.timerEnabled,
			toolbarLocation: this.state.toolbarLocation,
		});
		if (ServerResponse.isError(response)) {
			this.setState({
				snackbarOpen: true,
				snackbarMessage: "Saving your preferences failed. Please try again.",
			});
			return;
		}
	};

	onTimeLimitChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		let val = parseInt(e.target.value, 10);

		if (isNaN(val)) {
			val = 0;
		}

		this.setState({ countdownTime: val });

		const response = await this.props.savePresenterPreferences({
			countdownTime: val,
			fontSize: this.state.fontSize,
			timeMode: this.state.timeMode,
			timerEnabled: this.state.timerEnabled,
			toolbarLocation: this.state.toolbarLocation,
		});
		if (ServerResponse.isError(response)) {
			this.setState({
				snackbarOpen: true,
				snackbarMessage: "Saving your preferences failed. Please try again.",
			});
			return;
		}
	};

	resetTimer = () => {
		this.setState({ reset: this.state.reset + 1 });
	};

	increaseFontSize = async (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		e.stopPropagation();

		let fontSize = this.state.fontSize + 2;
		this.setState({ fontSize: fontSize });

		const response = await this.props.savePresenterPreferences({
			countdownTime: this.state.countdownTime,
			fontSize: fontSize,
			timeMode: this.state.timeMode,
			timerEnabled: this.state.timerEnabled,
			toolbarLocation: this.state.toolbarLocation,
		});
		if (ServerResponse.isError(response)) {
			this.setState({
				snackbarOpen: true,
				snackbarMessage: "Saving your preferences failed. Please try again.",
			});
			return;
		}
	};

	decreaseFontSize = async (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		e.stopPropagation();

		if (this.state.fontSize < 2) {
			return;
		}

		let fontSize = this.state.fontSize - 2;
		this.setState({ fontSize: fontSize });

		const response = await this.props.savePresenterPreferences({
			countdownTime: this.state.countdownTime,
			fontSize: fontSize,
			timeMode: this.state.timeMode,
			timerEnabled: this.state.timerEnabled,
			toolbarLocation: this.state.toolbarLocation,
		});
		if (ServerResponse.isError(response)) {
			this.setState({
				snackbarOpen: true,
				snackbarMessage: "Saving your preferences failed. Please try again.",
			});
			return;
		}
	};

	onGoBack = () => {
		if (this.props.location.state) {
			this.props.history.goBack();
		} else {
			this.props.history.push(`/build/${this.props.match.params.id}`);
		}
	};

	render() {
		const { classes } = this.props;
		if (this.state.pageNotFound) {
			return <PageNotFound errorMessage="We're sorry, but we couldn't find your Sermon Asset to present." />;
		}
		if (this.state.serverFailed) {
			return <ServerFailed errorMessage="We're sorry, but we're having trouble loading your Sermon Asset in the presenter." />;
		}
		return (
			<div className={classes.container}>
				<PresenterAppBar
					title={this.state.title}
					anchorElFont={this.state.anchorElFont}
					setAnchorElFont={this.setAnchorElFont}
					fontSize={this.state.fontSize}
					user={this.props.user}
					onSignIn={this.props.onSignIn}
					onSignOut={this.props.onSignOut}
					increaseFontSize={this.increaseFontSize}
					decreaseFontSize={this.decreaseFontSize}
					onGoBack={this.onGoBack}
				/>
				<LinearProgress color="primary" variant="determinate" value={this.state.scrollProgress} className={classes.progressBar} />
				<Drawer anchor="right" open={this.state.settingsOpen} onClose={() => this.toggleDrawer(false)}>
					<div tabIndex={0} role="button">
						<div className={classes.settingsListing}>
							<List component="nav">
								<FormControl className={classes.formControl}>
									<FormLabel>Toolbar Location</FormLabel>
									<RadioGroup
										aria-label="Location"
										name="location1"
										className={classes.group}
										value={ToolbarLocation[this.state.toolbarLocation]}
										onChange={(e) => this.handleBarLocationChange(e)}
										onClick={(event) => event.stopPropagation()}
									>
										<FormControlLabel
											value={ToolbarLocation[ToolbarLocation.Top]}
											control={<Radio color="primary" />}
											label="Top"
										/>
										<FormControlLabel
											value={ToolbarLocation[ToolbarLocation.Bottom]}
											control={<Radio color="primary" />}
											label="Bottom"
										/>
									</RadioGroup>
								</FormControl>
								<Divider />
								<FormControl className={classes.formControl}>
									<FormLabel>Time mode</FormLabel>
									<RadioGroup
										aria-label="gender"
										name="gender2"
										value={this.state.timeMode}
										onChange={(e, value) => this.onTimeModeChange(e, value)}
									>
										<FormControlLabel value={TimeMode.Clock} control={<Radio color="primary" />} label="Clock" />
										<FormControlLabel value={TimeMode.CountDown} control={<Radio color="primary" />} label="Count Down" />
										<FormControlLabel value={TimeMode.CountUp} control={<Radio color="primary" />} label="Count Up" />
										<FormControlLabel value={TimeMode.None} control={<Radio color="primary" />} label="None" />
									</RadioGroup>
									<br />
									{this.state.timeMode === "Count Down" && (
										<div>
											<TextField
												id="timeLimit"
												label="Time (minutes)"
												className={classes.textField}
												value={this.state.countdownTime}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.onTimeLimitChange(e)}
												margin="normal"
												variant="outlined"
												type="number"
											/>
										</div>
									)}
									<Button style={{ padding: "5px 0" }} onClick={() => this.resetTimer()}>
										<ReplayIcon />
										Reset Timer
									</Button>
								</FormControl>
							</List>
						</div>
					</div>
				</Drawer>
				{this.state.timeMode === TimeMode.None && (
					<div className={classes.timerIconWrapper} onClick={() => this.toggleDrawer(true)}>
						<TimerIcon className={classes.timerIcon} />
					</div>
				)}
				<Grid container direction="row" justifyContent="center" alignItems="flex-start">
					<Paper className={classes.sermonContent} style={{ fontSize: `${this.state.fontSize}px` }} id="content" elevation={2}>
						{this.state.loading ? (
							<div style={{ textAlign: "center" }}>
								<CircularProgress style={{ marginTop: "50px" }} />
								<br />
								<Typography variant="body1" color="textSecondary" style={{ textAlign: "center" }}>
									Loading...
								</Typography>
							</div>
						) : (
							<div dangerouslySetInnerHTML={this.state.body.length > 0 ? { __html: this.state.body } : undefined} />
						)}
					</Paper>
				</Grid>
				{this.state.timeMode !== TimeMode.None && (
					<PresenterToolBar
						key={this.state.reset}
						toggleDrawer={() => this.toggleDrawer(true)}
						barLocation={this.state.toolbarLocation}
						timeMode={this.state.timeMode}
						timeLimit={this.state.countdownTime}
					/>
				)}
				<Snackbar
					anchorOrigin={{
						vertical: "bottom",
						horizontal: "left",
					}}
					open={this.state.snackbarOpen}
					autoHideDuration={6000}
					onClose={() => this.setState({ snackbarOpen: false })}
					ContentProps={{
						"aria-describedby": "message-id",
					}}
					message={<span id="message-id">{this.state.snackbarMessage}</span>}
					action={[
						<span key="0" style={{ marginTop: "5px" }}>
							<IconButton key="close" aria-label="Close" color="inherit" onClick={() => this.setState({ snackbarOpen: false })}>
								<CloseIcon />
							</IconButton>
						</span>,
					]}
				/>
			</div>
		);
	}
}

interface CompatProps extends RouteComponentProps<Params> {}

export function PresenterHome(compatProps: CompatProps) {
	const isOffline = useOfflineStatus();
	const [config] = useAppConfig();
	const user = useAuthenticatedUser();
	const userInfo = useUser();
	const offlineAssets = useOfflineAssets();
	const classes = useStyles();

	const builderAssetId = compatProps.match.params.id;

	const [offlinePresenterPreferences, setOfflinePresentorPreferences] = useLocalStorage<PresenterPreferences>("offlinePresenterPreferences", {
		countdownTime: 30,
		fontSize: 20,
		timeMode: TimeMode.CountDown,
		timerEnabled: true,
		toolbarLocation: ToolbarLocation.Bottom,
	});

	const loadBuilderAsset = () => {
		if (isOffline || !offlineAssets.onlineMode) {
			const offlineAsset = offlineAssets.offlineBuilderAssets.find((a) => a.id === builderAssetId);

			if (offlineAsset) {
				const offlineSuccess: SuccessResult<BuilderAsset> = {
					data: offlineAsset,
					message: "",
				};
				return Promise.resolve(offlineSuccess);
			}

			const offlineError: ServerError = {
				statusCode: 404,
				message: "",
			};
			return Promise.resolve(offlineError);
		}

		return BuilderAssetService.get(builderAssetId);
	};

	const loadPresenterPreferences = async () => {
		if (isOffline || !offlineAssets.onlineMode) {
			const offlineSuccess: SuccessResult<PresenterPreferences> = {
				data: offlinePresenterPreferences,
				message: "",
			};
			return Promise.resolve(offlineSuccess);
		}

		const response = await PresenterPreferencesService.get();
		if (ServerResponse.isSuccess(response)) {
			setOfflinePresentorPreferences(response.data);
		}

		return response;
	};

	const savePresenterPreferences = async (prefs: PresenterPreferences) => {
		if (isOffline || !offlineAssets.onlineMode) {
			setOfflinePresentorPreferences(prefs);
			const offlineSuccess: SuccessResult<PresenterPreferences> = {
				data: prefs,
				message: "",
			};
			return Promise.resolve(offlineSuccess);
		}
		const response = await PresenterPreferencesService.save(prefs);
		if (ServerResponse.isSuccess(response)) {
			setOfflinePresentorPreferences(response.data);
		}
		return response;
	};

	const props: Props = {
		...compatProps,
		config,
		user,
		onSignIn: userInfo.updateUser,
		onSignOut: () => userInfo.updateUser(null),
		getBuilderAsset: loadBuilderAsset,
		getPresenterPreferences: loadPresenterPreferences,
		savePresenterPreferences,
	};

	return <HomeComponent {...props} classes={classes} />;
}